import { Injectable, inject, InjectionToken } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import type { Channel, PresenceChannel } from 'laravel-echo';
import type { Channel as PusherChannel, ChannelAuthorizationCallback } from 'pusher-js';
import { PUSHER_CONFIG } from './pusher.config';
import { IPusherService, PusherEventCallback } from './pusher.interface';

interface AuthData {
  auth: string;
  channel_data?: string;
  shared_secret?: string;
}

// Make Pusher available globally for Laravel Echo
declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

interface PusherAuthorizer {
  authorize: (socketId: string, callback: ChannelAuthorizationCallback) => void;
}

type AuthorizerFunction = (channel: PusherChannel) => PusherAuthorizer;

/**
 * Injection token for the Pusher service
 * Allows for easy mocking and testing
 */
export const PUSHER_SERVICE: InjectionToken<IPusherService> = new InjectionToken<IPusherService>('pusher.service');

@Injectable({
  providedIn: 'root',
})
export class PusherService implements IPusherService {
  // Using HttpClient directly (not HttpService) because Laravel's broadcasting
  // auth endpoint expects 'channel_name' and 'socket_id' with underscores,
  // not the kebab-case transformation that HttpService applies
  private readonly http = inject(HttpClient);
  private readonly config = inject(PUSHER_CONFIG);
  private readonly apiUrl: string = environment.api;
  private echo: Echo<'pusher'> | null = null;
  private initialized = false;

  constructor() {
    this.setupGlobalPusher();
  }

  private setupGlobalPusher(): void {
    if (typeof window !== 'undefined') {
      window.Pusher = Pusher;
    }
  }

  private lazilyInitializeEcho(): void {
    if (this.echo || !this.config.pusherKey) {
      return;
    }

    // Configure Pusher logging
    Pusher.logToConsole = this.config.enableLogging;

    // Create Pusher client with configuration
    const pusherClient: Pusher = new Pusher(this.config.pusherKey, {
      cluster: this.config.cluster,
      forceTLS: this.config.forceTLS,
      authorizer: this.createAuthorizer(),
    });

    this.echo = new Echo({broadcaster: 'pusher',client: pusherClient});

    this.initialized = true;
  }

  private createAuthorizer(): AuthorizerFunction {
    return (channel: PusherChannel) => {
      return {
        authorize: (socketId: string, callback: ChannelAuthorizationCallback) => {
          // Build full URL with API base (HttpService would add this automatically,
          // but we use HttpClient directly to avoid camelCase->kebab-case transformation)
          // Remove trailing slash from apiUrl to prevent double slashes
          const baseUrl: string = this.apiUrl.endsWith('/') ? this.apiUrl.slice(0, -1) : this.apiUrl;
          const fullUrl: string = `${baseUrl}${this.config.authEndpoint}`;

          this.http
            .post<AuthData>(fullUrl, {
              channel_name: channel.name,
              socket_id: socketId,
            })
            .subscribe({
              next: (authData: AuthData) => callback(null, authData),
              error: (error: Error) => {
                console.error('[Pusher] Authorization failed:', error);
                callback(error, null);
              },
            });
        },
      };
    };
  }

  listen<T = unknown>(
    channelName: string,
    eventName: string,
    callback: PusherEventCallback<T>
  ): void {
    this.lazilyInitializeEcho();
    if (!this.echo) {
      console.warn('[Pusher] Echo not initialized. Check pusherKey configuration.');
      return;
    }

    const channel: Channel = this.echo.channel(channelName);
    channel.listen(eventName, callback);
  }

  listenPrivate<T = unknown>(
    channelName: string,
    eventName: string,
    callback: PusherEventCallback<T>
  ): Channel | null {
    this.lazilyInitializeEcho();
    if (!this.echo) {
      console.warn('[Pusher] Echo not initialized. Check pusherKey configuration.');
      return null;
    }

    const privateChannel: Channel = this.echo.private(channelName);

    privateChannel.subscribed(() => {
      if (this.config.enableLogging) {
        console.log(`[Pusher] Successfully subscribed to private-${channelName}`);
      }
    });

    privateChannel.error((error: Error) => {
      console.error(`[Pusher] Subscription error for private-${channelName}:`, error);
    });

    // Bind the event using the underlying Pusher subscription
    // Note: This is a workaround for Laravel Echo's type definitions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (privateChannel as any).subscription?.bind(eventName, callback);

    return privateChannel;
  }

  listenPresence<T = unknown>(
    channelName: string,
    eventName: string,
    callback: PusherEventCallback<T>
  ): PresenceChannel | null {
    this.lazilyInitializeEcho();
    if (!this.echo) {
      console.warn('[Pusher] Echo not initialized. Check pusherKey configuration.');
      return null;
    }

    const presenceChannel: PresenceChannel = this.echo.join(channelName);

    if (this.config.enableLogging) {
      presenceChannel.here((members: unknown) => {
        console.log(`[Pusher] Current members in ${channelName}:`, members);
      });

      presenceChannel.joining((member: unknown) => {
        console.log(`[Pusher] Member joining ${channelName}:`, member);
      });

      presenceChannel.leaving((member: unknown) => {
        console.log(`[Pusher] Member leaving ${channelName}:`, member);
      });
    }

    // Bind the event
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (presenceChannel as any).subscription?.bind(eventName, callback);

    return presenceChannel;
  }

  leave(channelName: string): void {
    if (!this.echo) return;

    this.echo.leaveChannel(channelName);

    if (this.config.enableLogging) {
      console.log(`[Pusher] Left channel: ${channelName}`);
    }
  }

  disconnect(): void {
    if (!this.echo) return;

    this.echo.disconnect();
    this.echo = null;
    this.initialized = false;

    if (this.config.enableLogging) {
      console.log('[Pusher] Disconnected from all channels');
    }
  }

  getEnvironment(): string {
    return this.config.environment;
  }

  isReady(): boolean {
    return this.initialized && this.echo !== null;
  }
}
